
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ApiSysLocationService } from '@core/api/api-sys-location.service';
import { LanguageService } from '@core/i18n/language.service';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  startWith,
  Subject,
  switchMap,
  takeUntil
} from 'rxjs';

interface LocationResults {
  territories: string[];
  division: string[];
  municipality: string[];
}

@Component({
  selector: 'app-filter-location-search',
  standalone: true,
  imports: [ReactiveFormsModule],
  templateUrl: './location-search.component.html',
  styleUrls: ['./location-search.component.scss']
})
export class LocationSearchComponent implements OnInit, OnDestroy, OnChanges {
  locationResults: LocationResults = {
    territories: [],
    division: [],
    municipality: []
  };

  searchControl = new FormControl('');

  private selected = {
    territories: new Set<string>(),
    division: new Set<string>(),
    municipality: new Set<string>()
  };

  @Input() rows: any[] = [];
  @Input() placeholder?: string;
  @Input() initialSearchValue = '';
  @Input() checks: any;
  @Input() changeData = false;
  @Output() searchValueChange = new EventEmitter<string>();
  @Output() rowsEmit = new EventEmitter<any>();

  translateData: Record<string, string> = {};

  private destroy$ = new Subject<void>();

  constructor(
    private apiSysLocation: ApiSysLocationService,
    private cd: ChangeDetectorRef,
    private langService: LanguageService
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    console.log('changes', changes);
    if (changes['initialSearchValue']) {
      this.searchControl.setValue(changes['initialSearchValue'].currentValue);
    }
  }

  ngOnInit(): void {
    this.langService.getSharedDictionary('location-search')
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.translateData = data;
        this.cd.markForCheck();
      });

    this.searchControl.setValue(this.initialSearchValue || '');

    this.searchControl.valueChanges
      .pipe(
        startWith(this.initialSearchValue || ''),
        map((v) => (v || '').trim()),
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((value) => {

          this.searchValueChange.emit(value);

          if (!value || value.length < 3) {
            this.locationResults = { territories: [], division: [], municipality: [] };
            this.disCheckAll();
            this.selected = {
              territories: new Set<string>(),
              division: new Set<string>(),
              municipality: new Set<string>()
            };

            this.rowsEmit.emit({ rows: this.rows, selectedLocation: false, selected: this.selected });

            return [this.locationResults];
          }
          return this.apiSysLocation.searchFilterLocation(value);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (locations: any) => {
          this.locationResults = {
            territories: Array.isArray(locations?.territories) ? locations.territories : [],
            division: Array.isArray(locations?.division) ? locations.division : [],
            municipality: Array.isArray(locations?.municipality) ? locations.municipality : []
          };

          if (this.checks) {
            this.selected = this.checks;
          }

        },
        error: (err) => {
          console.error('Error fetching locations', err);
          this.locationResults = { territories: [], division: [], municipality: [] };
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  idFor(prefix: string, item: string): string {
    const slug = ('' + item)
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^\w\-]/g, '');
    return `${prefix}-${slug}`;
  }

  trackByFn(index: number, item: string) {
    return item ?? index;
  }

  hasResults(): boolean {
    const r = this.locationResults;
    return !!(
      r &&
      (r.territories?.length || r.division?.length || r.municipality?.length)
    );
  }

  onCheckboxChange(event: Event, category: 'territories' | 'division' | 'municipality', value: string) {
    const checked = (event.target as HTMLInputElement).checked;
    const set = this.selected[category];
    if (checked) set.add(value.trim().toLowerCase());
    else set.delete(value.trim().toLowerCase());

    this.applySelection();
  }

  isSelected(category: keyof typeof this.selected, value: string): boolean {
    return this.selected[category].has(value.trim().toLowerCase());
  }

  onToggleSelectAll(checked: boolean) {
    if (checked) {
      this.locationResults.territories?.forEach((t) => this.selected.territories.add(t.trim().toLowerCase()));
      this.locationResults.division?.forEach((d) => this.selected.division.add(d.trim().toLowerCase()));
      this.locationResults.municipality?.forEach((m) => this.selected.municipality.add(m.trim().toLowerCase()));
    } else {
      this.selected.territories.clear();
      this.selected.division.clear();
      this.selected.municipality.clear();
    }

    this.applySelection();
  }

  isAllSelected(): boolean {
    const r = this.locationResults;
    const allTerr = r.territories?.length ? r.territories.every((t) => this.selected.territories.has(t.trim().toLowerCase())) : true;
    const allDiv = r.division?.length ? r.division.every((d) => this.selected.division.has(d.trim().toLowerCase())) : true;
    const allMun = r.municipality?.length ? r.municipality.every((m) => this.selected.municipality.has(m.trim().toLowerCase())) : true;
    return allTerr && allDiv && allMun;
  }

  disCheckAll() {
    this.selected.territories.clear();
    this.selected.division.clear();
    this.selected.municipality.clear();
    this.cd.detectChanges();
  }

  private emitSelection(category: 'territories' | 'division' | 'municipality') {
    return [{ type: category, value: this.selected[category] }];
  }

  applySelection() {
    const terrSet = this.selected.territories;
    const divSet  = this.selected.division;
    const munSet  = this.selected.municipality;

    if (terrSet.size === 0 && divSet.size === 0 && munSet.size === 0) {
      this.rowsEmit.emit({ rows: this.rows, selectedLocation: false, selected: this.selected });
      return;
    }

    const rowsFiltred = this.rows.filter(row => {
      const value        = row.address;
      const territory    = value.adr_addterr?.trim().toLowerCase();
      const division     = value.adr_addprv?.trim().toLowerCase();
      const municipality = value.adr_addtwn?.trim().toLowerCase();

      const matchTerritory    = terrSet.size === 0 || (!!territory    && terrSet.has(territory));
      const matchDivision     = divSet.size  === 0 || (!!division     && [...divSet].some(i => i.split('|')[0].trim() === division));
      const matchMunicipality = munSet.size  === 0 || (!!municipality && [...munSet].some(i => i.split('|')[0].trim() === municipality));

      return matchTerritory && matchDivision && matchMunicipality;
    });

    this.rowsEmit.emit({ rows: rowsFiltred, selectedLocation: true, selected: this.selected });
  }

  clearSelection() {
    this.disCheckAll();
    this.rowsEmit.emit({ rows: this.rows, selectedLocation: false, selected: this.selected }); // Emit all rows back
  }
}
